import { lazy, ComponentType } from 'react';

// Helper to create lazy component with preload capability
type LazyComponentWithPreload<T extends ComponentType<any>> = ReturnType<typeof lazy<T>> & {
  preload: () => Promise<{ default: T }>;
};

const lazyWithPreload = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): LazyComponentWithPreload<T> => {
  const LazyComponent = lazy(importFn) as unknown as LazyComponentWithPreload<T>;
  LazyComponent.preload = importFn;
  return LazyComponent;
};

// ════════════════════════════════════════════════════════
// MODULE FEDERATION REMOTE IMPORTS
// Each page loads from its dedicated MFE via remoteEntry.js
// ════════════════════════════════════════════════════════

// Analytics MFE (:8084)
const Dashboard = lazyWithPreload(() => import('analytics_mfe/AnalyticsRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] Load failed:', err); return import('@/pages/NotFound'); }));
const CompetitiveAnalytics = Dashboard; // Handled by analytics routes
const AnalyticsDashboard = Dashboard;
const Targets = Dashboard;

// ESG Data MFE (:8082)
const EsgCategoryPage = lazyWithPreload(() => import('esg_data_mfe/EsgRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] Load failed:', err); return import('@/pages/NotFound'); }));
const Collections = EsgCategoryPage;
const BankingParameters = EsgCategoryPage;
const TargetData = EsgCategoryPage;

// Carbon Emission MFE (:8089) - dedicated carbon module
const CarbonEmissions = lazyWithPreload(() => import('carbon_mfe/CarbonRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] carbon_mfe load failed:', err); return import('@/pages/NotFound'); }));

// Supplier MFE (:8090) — registry, questionnaires, assessments, risk events,
// Scope 3 lines, Modern Slavery, payment practices, engagement programs.
// Backed by services/supplier on :3130.
const SupplierMfe = lazyWithPreload(() => import('supplier_mfe/SupplierRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] supplier_mfe load failed:', err); return import('@/pages/NotFound'); }));
const SuppliersDashboard = SupplierMfe;
const SuppliersList = SupplierMfe;
const SupplierDetail = SupplierMfe;
const SupplierBulkImport = SupplierMfe;
const QuestionnaireBuilder = SupplierMfe;
const AssignmentsCampaign = SupplierMfe;
const SupplierAssessments = SupplierMfe;
const SupplierRiskEvents = SupplierMfe;
const ModernSlavery = SupplierMfe;
const SupplierScope3 = SupplierMfe;
const PaymentPractices = SupplierMfe;
const EngagementPrograms = SupplierMfe;
const SupplierReports = SupplierMfe;

// LCA MFE (:8100) — products, life-cycle inventory, impact assessment,
// scenarios, EPD/DPP, assurance, audit. Backed by services/lca on :3140
// against the dedicated database `esg_caffe_lca`.
const LcaMfe = lazyWithPreload(() => import('lca_mfe/LcaRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] lca_mfe load failed:', err); return import('@/pages/NotFound'); }));
const LcaDashboardPage = LcaMfe;
const LcaProducts = LcaMfe;
const LcaProductDetailPage = LcaMfe;
const LcaProjects = LcaMfe;
const LcaProjectWizard = LcaMfe;
const LcaProjectDetailPage = LcaMfe;

// dMRV MFE (:8106) — digital MRV for carbon-removal projects (biochar first,
// Puro.earth Biochar 2025 v2). Backed by services/dmrv on :3180 against the
// dedicated database `esg_dmrv`. The MFE's routes.tsx dispatches on
// window.location.pathname for every /dmrv/* URL.
// CBAM (EU Carbon Border Adjustment Mechanism). Backed by services/cbam on :3190 against
// the dedicated database `esg_cbam`. The MFE's routes.tsx dispatches on the pathname for
// every /cbam/* URL; keys match super-admin cbam-menu-seed.service.ts.
const CbamMfe = lazyWithPreload(() => import('cbam_mfe/CbamRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] cbam_mfe load failed:', err); return import('@/pages/NotFound'); }));
const DmrvMfe = lazyWithPreload(() => import('dmrv_mfe/DmrvRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] dmrv_mfe load failed:', err); return import('@/pages/ModuleUnavailable').then(m => ({ default: m.DmrvUnavailable })); }));

// Reporting MFE (:8083)
const ReportGeneration = lazyWithPreload(() => import('reporting_mfe/ReportRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] Load failed:', err); return import('@/pages/NotFound'); }));
const SustainabilityReports = ReportGeneration;
const KpiMasterList = ReportGeneration;
const Documents = ReportGeneration;
// Framework Questions (formerly Filings in esg-data-mfe), Pending Reviews,
// Data Reviews, Report Audit, File Explorer, and Review History all live in
// reporting-mfe now — see refactor commit 37509b7.
const Filings = ReportGeneration;
const FrameworkQuestions = ReportGeneration;
const DataReviewsListing = ReportGeneration;
const FileExplorer = ReportGeneration;
const ReportAudit = ReportGeneration;
const PendingReviews = ReportGeneration;
const ReviewHistory = ReportGeneration;
// XBRL Support — taxonomy concept mappings + instance-document export. Seeded
// as the `/xbrl-support` menu by super-admin; served from reporting-mfe, which
// drives the four XBRL renderers in services/reporting.
const XbrlSupport = ReportGeneration;

// Compliance MFE (:8085)
const Materiality = lazyWithPreload(() => import('compliance_mfe/ComplianceRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] compliance_mfe load failed:', err); return import('@/pages/NotFound'); }));
const Assurance = Materiality;
const ReviewAndFeedback = Materiality;
const StakeholderSurvey = Materiality;
const ClimateDisclosures = Materiality; // AASB S2 / IFRS S2 lives inside compliance-mfe
const Csrd = Materiality; // CSRD / ESRS lives inside compliance-mfe
const EuTaxonomy = Materiality; // EU Taxonomy lives inside compliance-mfe

// Sustainability MFE (:8086)
const EmissionModelling = lazyWithPreload(() => import('sustainability_mfe/SustainabilityRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] Load failed:', err); return import('@/pages/NotFound'); }));
const NetZeroBenchmark = EmissionModelling;
const EsgRoadmap = EmissionModelling;
// RCO (Renewable Consumption Obligation) — lives inside sustainability_mfe,
// dispatched by pathname in sustainability-mfe/src/routes.tsx.
const RcoCompliance = EmissionModelling;

// Green Finance MFE (:8101) — green certifications + green loan book
const GreenFinanceRoutes = lazyWithPreload(() => import('green_finance_mfe/GreenFinanceRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] green_finance_mfe load failed:', err); return import('@/pages/NotFound'); }));
const GreenCertification = GreenFinanceRoutes;
const GreenLoanBook = GreenFinanceRoutes;

// Connectors MFE (:8105) — per-tenant integrations hub. REST, ERP, HRIS,
// utility, cloud, file-drop, and webhook connectors. Backed by
// services/connectors on :3170 against the dedicated database
// `esg_caffe_connectors`.
const ConnectorsRoutes = lazyWithPreload(() => import('connectors_mfe/ConnectorsRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] connectors_mfe load failed:', err); return import('@/pages/NotFound'); }));
const ConnectorsHub = ConnectorsRoutes;
const ConnectorNew = ConnectorsRoutes;
const ConnectorDetail = ConnectorsRoutes;

// PCF MFE (:8103) — Product Carbon Footprint. Catena-X CX-PCF Rulebook v4,
// SAMM PCF Aspect 9.0, PACT v3.0.3 exchange. Backed by services/pcf on :3141
// against the dedicated database `esg_caffe_pcf`.
const PcfMfe = lazyWithPreload(() => import('pcf_mfe/PcfRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] pcf_mfe load failed:', err); return import('@/pages/NotFound'); }));
const PcfDashboardPage = PcfMfe;
const PcfProductCatalog = PcfMfe;
const PcfProductCalculator = PcfMfe;

// ROC MFE (:8104) — Annual ROC Compliance Manager. Companies Act 2013 +
// MSMED Act + POSH Act + FEMA (FLA). Backed by services/roc-compliance on
// :3160 against dedicated database `esg_caffe_roc`. All /roc/* URLs
// federate to this MFE; internal routes.tsx dispatches by pathname.
const RocMfe = lazyWithPreload(() => import('roc_mfe/RocRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] roc_mfe load failed:', err); return import('@/pages/NotFound'); }));

// Chat MFE (:8087)
const Chat = lazyWithPreload(() => import('chat_mfe/ChatRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] Load failed:', err); return import('@/pages/NotFound'); }));
const ApiDocumentation = Chat;

// Settings MFE (:8088)
const CompanyProfile = lazyWithPreload(() => import('settings_mfe/SettingsRoutes').then(m => ({ default: m.default })).catch((err) => { console.error('[MFE] Load failed:', err); return import('@/pages/NotFound'); }));
const Team = CompanyProfile;
const HelpSupport = CompanyProfile;
const Integrations = CompanyProfile;
const Publisher = CompanyProfile;
const AccessReview = CompanyProfile;

// Placeholder for modules that have a menu entry but no implementation yet
// (currently used by Supplier Hub sub-routes — suppliers, questionnaires,
// campaigns, assessments, risk events, modern slavery, scope-3 emissions,
// payment practices, engagement programs, reports).
const ComingSoon = lazyWithPreload(() => import('@/pages/ComingSoon'));

// ════════════════════════════════════════════════════════
// COMPONENT MAP — maps database componentKey to MFE component
// DynamicRoute uses this to resolve which component to render
// ════════════════════════════════════════════════════════

export const componentMap: Record<string, ComponentType<any>> = {
  // Dashboard & Analytics
  'dashboard': Dashboard,
  'overview': Dashboard,
  'analytics': AnalyticsDashboard,
  'caffe-analytics': AnalyticsDashboard,
  'caffe_analytics': AnalyticsDashboard,
  'competitive': CompetitiveAnalytics,
  'competitive-analytics': CompetitiveAnalytics,
  'competitive_analytics': CompetitiveAnalytics,
  'targets': Targets,
  'actions': Targets,

  // ESG Categories
  'environmental': EsgCategoryPage,
  'social': EsgCategoryPage,
  'governance': EsgCategoryPage,
  // Supply chain / supplier domain — now served by supplier_mfe (was a
  // placeholder under esg_data_mfe previously).
  'supply-chain': SuppliersList,
  'supply_chain': SuppliersList,
  'carbon': CarbonEmissions,
  // CCTS domain lives INSIDE apps/carbon-mfe (same bundle, dispatched by URL).
  // The 6 children menu rows seeded by super-admin's CctsService.seedCctsMenu
  // (Overview/GEI/Factors/Ledger/Compliance/Exports) all federate to carbon_mfe.
  'ccts':             CarbonEmissions,
  'ccts-overview':    CarbonEmissions,
  'ccts_overview':    CarbonEmissions,
  'ccts-gei':         CarbonEmissions,
  'ccts_gei':         CarbonEmissions,
  'ccts-factors':     CarbonEmissions,
  'ccts_factors':     CarbonEmissions,
  'ccts-ledger':      CarbonEmissions,
  'ccts_ledger':      CarbonEmissions,
  'ccts-compliance':  CarbonEmissions,
  'ccts_compliance':  CarbonEmissions,
  'ccts-exports':     CarbonEmissions,
  'ccts_exports':     CarbonEmissions,
  // 'carbon_accounting' sits under the ANALYTICS > Environmental section, so it
  // renders the ESG analytics category page (charts) like Energy/Water/Waste.
  // The Scope 1/2/3 footprinting data-entry tool is reached via the Data
  // Collection menu keys below ('carbon_emission' / 'carbon-emissions').
  'carbon_accounting': EsgCategoryPage,
  'energy': EsgCategoryPage,
  'water': EsgCategoryPage,
  'waste': EsgCategoryPage,
  'biodiversity': EsgCategoryPage,
  'workforce': EsgCategoryPage,
  'workspace': EsgCategoryPage,
  'dei': EsgCategoryPage,
  'health-safety': EsgCategoryPage,
  'healthsafety': EsgCategoryPage,
  'health&safety': EsgCategoryPage,
  'training': EsgCategoryPage,
  'human-rights': EsgCategoryPage,
  'human_right': EsgCategoryPage,
  'human_rights': EsgCategoryPage,
  'community': EsgCategoryPage,
  'board': EsgCategoryPage,
  'ethics': EsgCategoryPage,
  'compensation': EsgCategoryPage,
  'risk': EsgCategoryPage,
  'policies': EsgCategoryPage,
  // Supplier domain — all routed to supplier_mfe; the MFE's routes.tsx
  // dispatches to the right page based on window.location.pathname.
  'suppliers': SuppliersList,
  'supplier-detail': SupplierDetail,
  'supplier_detail': SupplierDetail,
  'supplier-dashboard': SuppliersDashboard,
  'supplier_dashboard': SuppliersDashboard,
  'supplier-import': SupplierBulkImport,
  'supplier_import': SupplierBulkImport,
  'supplier-questionnaires': QuestionnaireBuilder,
  'supplier_questionnaires': QuestionnaireBuilder,
  'supplier-campaigns': AssignmentsCampaign,
  'supplier_campaigns': AssignmentsCampaign,
  'supplier-assessments': SupplierAssessments,
  'supplier_assessments': SupplierAssessments,
  'supplier-risk-events': SupplierRiskEvents,
  'supplier_risk_events': SupplierRiskEvents,
  'modern-slavery': ModernSlavery,
  'modern_slavery': ModernSlavery,
  'supplier-scope3': SupplierScope3,
  'supplier_scope3': SupplierScope3,
  'supplier-payment-practices': PaymentPractices,
  'supplier_payment_practices': PaymentPractices,
  'supplier-engagement': EngagementPrograms,
  'supplier_engagement': EngagementPrograms,
  'supplier-reports': SupplierReports,
  'supplier_reports': SupplierReports,
  // Supplier PCF requests (Phase 3 — services/supplier + services/lca)
  'supplier-pcf-requests': SupplierMfe,
  'supplier_pcf_requests': SupplierMfe,
  'pcf-requests': SupplierMfe,
  // LCA domain — all routed to lca_mfe; the MFE's routes.tsx dispatches by
  // window.location.pathname. The DB menu seed should register 'lca' as the
  // componentKey on the LCA menu item.
  'lca': LcaMfe,
  'life-cycle-assessment': LcaMfe,
  'life_cycle_assessment': LcaMfe,
  'lca-dashboard': LcaDashboardPage,
  'lca_dashboard': LcaDashboardPage,
  'lca-products': LcaProducts,
  'lca_products': LcaProducts,
  'lca-product-detail': LcaProductDetailPage,
  'lca_product_detail': LcaProductDetailPage,
  'lca-projects': LcaProjects,
  'lca_projects': LcaProjects,
  'lca-project-wizard': LcaProjectWizard,
  'lca_project_wizard': LcaProjectWizard,
  // Custom Compliance — tenant-owned compliance frameworks; a page of reporting_mfe
  // (src/compliance-studio), backed by the compliance-studio module of services/reporting.
  'compliance_studio': ReportGeneration,
  'compliance-studio': ReportGeneration,
  // CBAM domain — all routed to cbam_mfe.
  'cbam': CbamMfe,
  'cbam_dashboard': CbamMfe,
  'cbam-dashboard': CbamMfe,
  'cbam_profile': CbamMfe,
  'cbam-profile': CbamMfe,
  'cbam_imports': CbamMfe,
  'cbam-imports': CbamMfe,
  'cbam_purchase_orders': CbamMfe,
  'cbam-purchase-orders': CbamMfe,
  'cbam_suppliers': CbamMfe,
  'cbam-suppliers': CbamMfe,
  'cbam_data_sets': CbamMfe,
  'cbam-data-sets': CbamMfe,
  'cbam_cost_summary': CbamMfe,
  'cbam-cost-summary': CbamMfe,
  'cbam_threshold': CbamMfe,
  'cbam-threshold': CbamMfe,
  'cbam_certificates': CbamMfe,
  'cbam-certificates': CbamMfe,
  'cbam_declarations': CbamMfe,
  'cbam-declarations': CbamMfe,
  'cbam_verification': CbamMfe,
  'cbam-verification': CbamMfe,
  'cbam_operator': CbamMfe,
  'cbam-operator': CbamMfe,
  'cbam_evidence': CbamMfe,
  'cbam-evidence': CbamMfe,
  'cbam_scenarios': CbamMfe,
  'cbam_compare': CbamMfe,
  'cbam_hedging': CbamMfe,
  'cbam_buying_guide': CbamMfe,
  'cbam_ai': CbamMfe,
  'cbam_groups': CbamMfe,
  'cbam-scenarios': CbamMfe,
  'cbam_alerts': CbamMfe,
  'cbam-alerts': CbamMfe,
  'cbam_catalogue': CbamMfe,
  'cbam-catalogue': CbamMfe,
  'cbam_reports': CbamMfe,
  'cbam-reports': CbamMfe,
  'cbam_audit': CbamMfe,
  // navigation groups (super-admin cbam-menu-seed GROUPS)
  'cbam_setup': CbamMfe, 'cbam_trade': CbamMfe, 'cbam_supply': CbamMfe, 'cbam_emissions': CbamMfe, 'cbam_finance': CbamMfe, 'cbam_compliance': CbamMfe, 'cbam_tools': CbamMfe,
  'cbam-audit': CbamMfe,

  // dMRV domain — all routed to dmrv_mfe. Register 'dmrv' as the componentKey
  // on the dMRV menu item (path /dmrv); sub-items use the keys below.
  'dmrv': DmrvMfe,
  'dmrv-dashboard': DmrvMfe,
  'dmrv_dashboard': DmrvMfe,
  'dmrv-projects': DmrvMfe,
  'dmrv_projects': DmrvMfe,
  'dmrv-measurements': DmrvMfe,
  'dmrv_measurements': DmrvMfe,
  'dmrv-evidence': DmrvMfe,
  'dmrv_evidence': DmrvMfe,
  'dmrv-parties': DmrvMfe,
  'dmrv_parties': DmrvMfe,
  'dmrv-methodologies': DmrvMfe,
  'dmrv_methodologies': DmrvMfe,
  'dmrv-audit': DmrvMfe,
  'dmrv_audit': DmrvMfe,
  // navigation groups (super-admin dmrv-menu-seed GROUPS)
  'dmrv_setup': DmrvMfe, 'dmrv_operations': DmrvMfe, 'dmrv_chain': DmrvMfe, 'dmrv_monitoring': DmrvMfe, 'dmrv_accounting': DmrvMfe, 'dmrv_assurance': DmrvMfe, 'dmrv_market': DmrvMfe, 'dmrv_tools': DmrvMfe,
  // Phases 2–15 pages (all dispatch inside dmrv_mfe on the pathname); keys
  // match super-admin dmrv-menu-seed.service.ts.
  'dmrv-feedstock': DmrvMfe,
  'dmrv_feedstock': DmrvMfe,
  'dmrv-production': DmrvMfe,
  'dmrv_production': DmrvMfe,
  'dmrv-quality': DmrvMfe,
  'dmrv_quality': DmrvMfe,
  'dmrv-inventory': DmrvMfe,
  'dmrv_inventory': DmrvMfe,
  'dmrv-end-use': DmrvMfe,
  'dmrv_end_use': DmrvMfe,
  'dmrv-trace': DmrvMfe,
  'dmrv_trace': DmrvMfe,
  'dmrv-periods': DmrvMfe,
  'dmrv_periods': DmrvMfe,
  'dmrv-exceptions': DmrvMfe,
  'dmrv_exceptions': DmrvMfe,
  'dmrv-calculations': DmrvMfe,
  'dmrv_calculations': DmrvMfe,
  'dmrv-verification': DmrvMfe,
  'dmrv_verification': DmrvMfe,
  'dmrv-vvb': DmrvMfe,
  'dmrv_vvb': DmrvMfe,
  'dmrv-credits': DmrvMfe,
  'dmrv_credits': DmrvMfe,
  'dmrv-alerts': DmrvMfe,
  'dmrv_alerts': DmrvMfe,
  'dmrv-data-quality': DmrvMfe,
  'dmrv_data_quality': DmrvMfe,
  'dmrv-map': DmrvMfe,
  'dmrv_map': DmrvMfe,
  'dmrv-registry': DmrvMfe,
  'dmrv_registry': DmrvMfe,
  'dmrv-reversals': DmrvMfe,
  'dmrv_reversals': DmrvMfe,
  'dmrv-ai': DmrvMfe,
  'dmrv_ai': DmrvMfe,
  'dmrv-agroforestry': DmrvMfe,
  'dmrv_agroforestry': DmrvMfe,
  'dmrv-marketplace': DmrvMfe,
  'dmrv_marketplace': DmrvMfe,
  'dmrv-devices': DmrvMfe,
  'dmrv_devices': DmrvMfe,
  'dmrv-lab': DmrvMfe,
  'dmrv_lab': DmrvMfe,
  'lca-project-detail': LcaProjectDetailPage,
  'lca_project_detail': LcaProjectDetailPage,

  // PCF domain — routed to pcf_mfe; the MFE's routes.tsx dispatches by
  // window.location.pathname. Register 'pcf' as the componentKey on the PCF
  // menu item in the DB menu seed.
  'pcf': PcfMfe,
  'product-carbon-footprint': PcfMfe,
  'product_carbon_footprint': PcfMfe,
  'pcf-dashboard': PcfDashboardPage,
  'pcf_dashboard': PcfDashboardPage,
  // Legacy: 'pcf-products' pointed to a standalone catalog component before
  // the v2 IA moved everything under the pcf-mfe. Now federates to pcf-mfe.
  'pcf-products': PcfMfe,
  'pcf_products': PcfMfe,
  'pcf-calculator': PcfProductCalculator,
  'pcf_calculator': PcfProductCalculator,
  // Five PCF sub-category children seeded by super-admin (see
  // super-admin/backend/src/modules/pcf/pcf.service.ts seedPcfMenu).
  // All five federate to the same pcf_mfe — the MFE dispatches to the
  // right page based on window.location.pathname.
  'pcf-overview':   PcfMfe,
  'pcf_overview':   PcfMfe,
  'pcf-calculate':  PcfMfe,
  'pcf_calculate':  PcfMfe,
  'pcf-compliance': PcfMfe,
  'pcf_compliance': PcfMfe,
  'pcf-ai':         PcfMfe,
  'pcf_ai':         PcfMfe,
  'pcf-exchange':   PcfMfe,
  'pcf_exchange':   PcfMfe,
  // Dedicated audit log page under the Compliance category.
  'pcf-audit':      PcfMfe,
  // v2 IA — 7-section persona-scoped navigation (Sprint 5).
  // Every section federates to the same pcf-mfe; its internal router
  // dispatches on window.location.pathname to the right page.
  'pcf_my_work':    PcfMfe,
  'pcf_build':      PcfMfe,
  'pcf_ai':         PcfMfe,
  'pcf_analyze':    PcfMfe,
  'pcf_governance': PcfMfe,
  'pcf_admin':      PcfMfe,

  // ROC domain — routed to roc_mfe; the MFE's routes.tsx dispatches by
  // window.location.pathname. Six sub-category children seeded by super-admin
  // (see super-admin/backend/src/modules/roc/roc.service.ts seedRocMenu).
  'roc':             RocMfe,
  'roc-overview':    RocMfe,
  'roc_overview':    RocMfe,
  'roc-companies':   RocMfe,
  'roc_companies':   RocMfe,
  'roc-compliances': RocMfe,
  'roc_compliances': RocMfe,
  'roc-events':      RocMfe,
  'roc_events':      RocMfe,
  'roc-registers':   RocMfe,
  'roc_registers':   RocMfe,
  'roc-exports':     RocMfe,
  'roc_exports':     RocMfe,
  'pcf_audit':      PcfMfe,
  'risk_assessment': EsgCategoryPage,
  'risk-assessment': EsgCategoryPage,
  'assessments': SupplierAssessments,
  'scope_3_data': SupplierScope3,
  'scope-3-data': SupplierScope3,

  // Supplier Hub sub-modules — each menu item routes to its own page inside supplier-mfe
  // (supplier-mfe's routes.tsx dispatches by pathname to the correct page component)
  'questionnaires': SupplierMfe,
  'campaigns': SupplierMfe,
  'risk-events': SupplierMfe,
  'risk_events': SupplierMfe,
  'scope-3-emissions': SupplierMfe,
  'scope_3_emissions': SupplierMfe,
  'payment-practices': SupplierMfe,
  'payment_practices': SupplierMfe,
  'engagement-programs': SupplierMfe,
  'engagement_programs': SupplierMfe,

  // Data Collection
  'carbon_emission': CarbonEmissions,
  'carbon-emissions': CarbonEmissions,
  'target_data': TargetData,
  'target-data': TargetData,
  'target_and_action': TargetData,
  'target-and-action': TargetData,
  'targetandaction': TargetData,
  'targets_and_actions': TargetData,
  'targets-and-actions': TargetData,
  'fillings': Filings,
  'filings': Filings,
  'framework_questions': FrameworkQuestions,
  'framework-questions': FrameworkQuestions,
  'collections': Collections,
  'banking-parameters': BankingParameters,
  'banking_parameters': BankingParameters,

  // Reporting
  'generation': ReportGeneration,
  'report-generation': ReportGeneration,
  'sustainability-reports': SustainabilityReports,
  'sustainability_reports': SustainabilityReports,
  'kpi-master': KpiMasterList,
  'kpi_master': KpiMasterList,
  'documents': Documents,
  'xbrl-support': XbrlSupport,
  'xbrl_support': XbrlSupport,
  'xbrl': XbrlSupport,

  // Water & Effluent Compliance — routes into compliance-mfe (Materiality alias)
  // which dispatches by pathname to WaterSetupWizard / WaterConsents / WaterEvents.
  'water_portfolio':   Materiality,
  'water-portfolio':   Materiality,
  'water_setup':       Materiality,
  'water-setup':       Materiality,
  'water_consents':    Materiality,
  'water-consents':    Materiality,
  'water_monitoring':  Materiality,
  'water-monitoring':  Materiality,
  'water_operations':  Materiality,
  'water-operations':  Materiality,
  'water_events':      Materiality,
  'water-events':      Materiality,
  'water_reports':     Materiality,
  'water-reports':     Materiality,
  'water_form_v':      Materiality,
  'water-form-v':      Materiality,
  'water_inspection_pack': Materiality,
  'water-inspection-pack': Materiality,

  // Compliance & Materiality
  'materiality': Materiality,
  'materiality_assessment': Materiality,
  'materiality-assessment': Materiality,
  'climate_disclosures': ClimateDisclosures,
  'climate-disclosures': ClimateDisclosures,
  'aasb_s2': ClimateDisclosures,
  'aasb-s2': ClimateDisclosures,
  'ifrs_s2': ClimateDisclosures,
  'ifrs-s2': ClimateDisclosures,
  'tcfd': ClimateDisclosures,
  'csrd': Csrd,
  'esrs': Csrd,
  'csrd_esrs': Csrd,
  'csrd-esrs': Csrd,
  'eu_taxonomy': EuTaxonomy,
  'eu-taxonomy': EuTaxonomy,
  'taxonomy': EuTaxonomy,
  // GRI 2021 Standards — inside compliance-mfe (routes to Gri.tsx by pathname).
  'gri': Materiality,
  'gri_2021': Materiality,
  'gri-2021': Materiality,
  // BRSR (India) — inside compliance-mfe.
  'brsr': Materiality,
  'brsr_core': Materiality,
  'brsr-core': Materiality,
  // UK SDR + TPT — inside compliance-mfe.
  'uk_sdr': Materiality,
  'uk-sdr': Materiality,
  'sdr': Materiality,
  'tpt': Materiality,
  'uk_sdr_tpt': Materiality,
  'uk-sdr-tpt': Materiality,
  // SEC Climate (Reg S-K subpart 1500 + Reg S-X Article 14) — inside compliance-mfe.
  'sec_climate': Materiality,
  'sec-climate': Materiality,
  'sec_climate_disclosure': Materiality,
  'sec-climate-disclosure': Materiality,
  'reg_sk': Materiality,
  'reg-sk': Materiality,
  'reg_sx': Materiality,
  'reg-sx': Materiality,
  // Data-quality dashboard — cross-framework coverage + Scope reconciliation.
  'data_quality': Materiality,
  'data-quality': Materiality,
  'data_quality_dashboard': Materiality,
  'data-quality-dashboard': Materiality,
  // Multi-entity consolidation (group tree + GHG rollup).
  'consolidation': Materiality,
  'group_consolidation': Materiality,
  'group-consolidation': Materiality,
  // Reviewer-side assurance sample workflow — companion to the existing Assurance page.
  'assurance_samples': Materiality,
  'assurance-samples': Materiality,
  'assurance': Assurance,
  'reviewandfeedback': ReviewAndFeedback,
  'review-and-feedback': ReviewAndFeedback,
  'reviews_listing': DataReviewsListing,
  'reviews-listing': DataReviewsListing,
  'data_reviews': DataReviewsListing,
  'data-reviews': DataReviewsListing,
  'file_explorer': FileExplorer,
  'file-explorer': FileExplorer,
  'report_audit': ReportAudit,
  'report-audit': ReportAudit,
  'pending_reviews': PendingReviews,
  'pending-reviews': PendingReviews,
  'review_history': ReviewHistory,
  'review-history': ReviewHistory,
  'stakeholder-survey': StakeholderSurvey,

  // RCO — Renewable Consumption Obligation (BEE). All /rco-energy/* paths
  // federate to sustainability_mfe; routes.tsx dispatches by pathname.
  'rco-energy':      RcoCompliance,
  'rco_energy':      RcoCompliance,
  'rco-compliance':  RcoCompliance,
  'rco_compliance':  RcoCompliance,

  // Sustainability
  'emission-modelling': EmissionModelling,
  'emission_modelling': EmissionModelling,
  'net-zero-benchmark': NetZeroBenchmark,
  'net_zero_benchmark': NetZeroBenchmark,
  'esg-roadmap': EsgRoadmap,
  'esg_roadmap': EsgRoadmap,
  'strategic_roadmap': EsgRoadmap,
  'strategic-roadmap': EsgRoadmap,
  'strategicroadmap': EsgRoadmap,
  'esg_strategic_roadmap': EsgRoadmap,
  'esg-strategic-roadmap': EsgRoadmap,
  'green-certification': GreenCertification,
  'green_certification': GreenCertification,
  'green-loan-book': GreenLoanBook,
  'green_loan_book': GreenLoanBook,

  // Connectors — per-tenant integrations hub (connectors_mfe :8105)
  'connectors': ConnectorsHub,
  'connector': ConnectorsHub,
  'connectors-hub': ConnectorsHub,
  'connectors_hub': ConnectorsHub,
  'integrations': ConnectorsHub,
  'connector-new': ConnectorNew,
  'connector_new': ConnectorNew,
  'connector-detail': ConnectorDetail,
  'connector_detail': ConnectorDetail,

  // Waste & Compliance — lives inside sustainability_mfe (WastePage), routed
  // by pathname. Multiple key variants so DB menu seeds work regardless of
  // casing/hyphen choice.
  'waste':                EmissionModelling,
  'waste-compliance':     EmissionModelling,
  'waste_compliance':     EmissionModelling,
  'waste-management':     EmissionModelling,
  'waste_management':     EmissionModelling,
  'wastecompliance':      EmissionModelling,
  'wastemanagement':      EmissionModelling,

  // Chat & AI
  'chat_and_esgai': Chat,
  'chat-and-esgai': Chat,
  'chart_and_esgai': Chat,
  'communication': Chat,
  'chat': Chat,
  'esgai': Chat,
  'api-documentation': ApiDocumentation,

  // Settings & Admin
  'settings': CompanyProfile,
  'company_profile': CompanyProfile,
  'company-profile': CompanyProfile,
  // Personal Profile as its own menu item (settings-mfe renders single-tab mode via URL)
  'profile': CompanyProfile,
  'personal_profile': CompanyProfile,
  'personal-profile': CompanyProfile,
  // General as its own menu item
  'general': CompanyProfile,
  'general_settings': CompanyProfile,
  'general-settings': CompanyProfile,
  'term_permission': Team,
  'team': Team,
  'org_setup': Team,
  'org-setup': Team,
  'help_support': HelpSupport,
  'help-support': HelpSupport,
  'integrations': Integrations,
  'administration': Publisher,
  'publisher': Publisher,
  'admin_publisher': Publisher,
  'access_review': AccessReview,
  'access-review': AccessReview,
  'dormancy_review': AccessReview,
  // Activity log (tenant audit trail) — rendered by settings-mfe via URL match.
  // Deliberately no bare 'audit' alias: other domains have an /audit sub-page.
  'activity_log': CompanyProfile,
  'activity-log': CompanyProfile,
};

/**
 * Look up a component by its database key.
 * Returns undefined if no match found.
 */
export function getComponentByKey(key: string | null, pathFallback?: string): ComponentType<any> | undefined {
  // Try componentKey from database first
  if (key) {
    const normalized = key.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (componentMap[key]) return componentMap[key];
    if (componentMap[normalized]) return componentMap[normalized];

    const stripped = normalized.replace(/[-_]/g, '');
    for (const [mapKey, component] of Object.entries(componentMap)) {
      if (mapKey.replace(/[-_]/g, '') === stripped) return component;
    }
  }

  // Fallback: try to match using URL path segments
  if (pathFallback) {
    const normalizedPath = pathFallback.toLowerCase();
    if (
      normalizedPath.includes('/target_and_action') ||
      normalizedPath.includes('/target-and-action') ||
      normalizedPath.includes('/targetdata') ||
      normalizedPath.includes('/targetandaction') ||
      normalizedPath.includes('/target_data') ||
      normalizedPath.includes('/target-data')
    ) {
      console.log('[routeComponents] Explicit target/actions fallback matched:', pathFallback);
      return componentMap['target_and_action'] || componentMap['target-data'] || componentMap['targetdata'] || componentMap['target_data'];
    }

    // Any URL under /pcf belongs to the PCF MFE. Without this guard the
    // last-segment fallback below picks up 'overview' → analytics Dashboard.
    if (normalizedPath === '/pcf' || normalizedPath.startsWith('/pcf/')) {
      console.log('[routeComponents] PCF prefix matched:', pathFallback);
      return componentMap['pcf'];
    }

    // Same treatment for /roc — all sub-paths go to roc_mfe.
    if (normalizedPath === '/roc' || normalizedPath.startsWith('/roc/')) {
      console.log('[routeComponents] ROC prefix matched:', pathFallback);
      return componentMap['roc'];
    }

    // XBRL Support — /xbrl-support and any sub-path belong to reporting_mfe.
    // The last-segment fallback below cannot help here: a URL such as
    // /xbrl-support/concepts would match on 'concepts', which is not a
    // componentMap key, and the page would 404.
    if (normalizedPath === '/xbrl-support' || normalizedPath.startsWith('/xbrl')) {
      console.log('[routeComponents] XBRL prefix matched:', pathFallback);
      return componentMap['xbrl-support'];
    }

    // Connectors — all /connectors/* sub-paths belong to connectors_mfe.
    // Without this guard, /connectors/new's last segment 'new' would fall
    // through to the generic segment-match logic and could land on the
    // wrong component.
    if (normalizedPath === '/connectors' || normalizedPath.startsWith('/connectors/')) {
      console.log('[routeComponents] Connectors prefix matched:', pathFallback);
      return componentMap['connectors'];
    }

    // Same treatment for /ccts — CCTS lives inside carbon_mfe (shared bundle,
    // routed by pathname). Without this guard, /ccts/overview matches the
    // analytics dashboard on the 'overview' last segment.
    if (normalizedPath === '/ccts' || normalizedPath.startsWith('/ccts/')) {
      console.log('[routeComponents] CCTS prefix matched:', pathFallback);
      return componentMap['ccts'];
    }

    // Carbon Emissions sub-modules — /carbon/dashboard, /carbon/scope-1, … all
    // live inside carbon_mfe and are dispatched there by pathname. This guard
    // is what makes them resolvable at all: the fallback below keys off the
    // LAST path segment, so '/carbon/dashboard' would match the analytics
    // 'dashboard' entry and '/carbon/reports' the reporting module — each
    // sub-module silently rendering somebody else's page.
    if (normalizedPath === '/carbon' || normalizedPath.startsWith('/carbon/')) {
      console.log('[routeComponents] Carbon prefix matched:', pathFallback);
      return componentMap['carbon'];
    }

    // RCO — all /rco-energy/* paths go to sustainability_mfe.
    if (normalizedPath === '/rco-energy' || normalizedPath.startsWith('/rco-energy/')) {
      console.log('[routeComponents] RCO prefix matched:', pathFallback);
      return componentMap['rco-energy'];
    }

    // Water & Effluent — all /water/* paths belong to compliance_mfe (Materiality
    // federation entry). Without this guard, /water/portfolio falls through to
    // the segment loop and matches the legacy 'water' → EsgCategoryPage mapping,
    // which then shows "Category not found." for anything except the classic
    // ESG water data-collection category.
    if (normalizedPath === '/water' || normalizedPath.startsWith('/water/')) {
      console.log('[routeComponents] Water prefix matched:', pathFallback);
      return componentMap['water_portfolio'] || componentMap['materiality'];
    }

    // Waste & Compliance — all /waste/* sub-paths belong to sustainability_mfe
    // (WastePage). Without this guard, /waste/dashboard's last segment
    // 'dashboard' matches componentMap['dashboard'] → generic Analytics
    // Dashboard, and /waste/reports matches the framework reports page, etc.
    if (normalizedPath === '/waste' || normalizedPath.startsWith('/waste/')) {
      console.log('[routeComponents] Waste prefix matched:', pathFallback);
      return componentMap['waste-compliance'] || componentMap['waste'];
    }

    // Hardcoded Analytics bundle (Sidebar.tsx "hardcoded-analytics-*" entries,
    // apps/shell/src/components/Sidebar.tsx): /analytics/environmental,
    // /analytics/social, /analytics/governance. Without this guard the last
    // segment ('environmental'/'social'/'governance') matches the legacy
    // single-fine-category componentMap entries meant for esg_data_mfe's
    // /environmental/<category> URLs (e.g. /environmental/energy), and
    // esg_data_mfe's EsgCategoryPage can't resolve a whole PILLAR (only one
    // fine-grained category name at a time) → renders "Category not found."
    // Route these 3 to the analytics_mfe dashboard instead, which already
    // has E/S/G tab filtering built in (AnalyticsDashboard.tsx).
    if (
      normalizedPath === '/analytics/environmental' ||
      normalizedPath === '/analytics/social' ||
      normalizedPath === '/analytics/governance'
    ) {
      console.log('[routeComponents] Analytics pillar-bundle prefix matched:', pathFallback);
      return componentMap['analytics'];
    }

    // dMRV — every /dmrv/* URL belongs to dmrv_mfe (its routes.tsx dispatches
    // on the pathname). Without this guard the segment loop tries the LAST
    // segment first, so /dmrv/data-quality matches the compliance 'data-quality'
    // key and /dmrv/alerts, /dmrv/evidence, /dmrv/audit etc. would land in
    // other domains' pages.
    if (normalizedPath === '/dmrv' || normalizedPath.startsWith('/dmrv/')) {
      console.log('[routeComponents] dMRV prefix matched:', pathFallback);
      return componentMap['dmrv'];
    }

    // CBAM — every /cbam/* URL belongs to cbam_mfe (same reasoning as dMRV: the
    // last segment 'audit', 'alerts', 'reports', 'catalogue' exists in other domains).
    if (normalizedPath === '/cbam' || normalizedPath.startsWith('/cbam/')) {
      console.log('[routeComponents] CBAM prefix matched:', pathFallback);
      return componentMap['cbam'];
    }

    const segments = normalizedPath.replace(/^\//, '').split('/');
    console.log('[routeComponents] Path fallback:', pathFallback, 'segments:', segments);

    // Try last segment, then second-to-last, then third-to-last, then first
    for (const idx of [segments.length - 1, segments.length - 2, segments.length - 3, 0]) {
      if (idx >= 0 && idx < segments.length) {
        const seg = segments[idx];
        if (seg && componentMap[seg]) {
          console.log('[routeComponents] Matched segment:', seg);
          return componentMap[seg];
        }

        // Try with underscores replaced by hyphens and vice versa
        const altSeg = seg.replace(/_/g, '-');
        if (componentMap[altSeg]) {
          console.log('[routeComponents] Matched alt segment:', altSeg);
          return componentMap[altSeg];
        }
        const altSeg2 = seg.replace(/-/g, '_');
        if (componentMap[altSeg2]) {
          console.log('[routeComponents] Matched alt2 segment:', altSeg2);
          return componentMap[altSeg2];
        }
      }
    }

    if (/target(?:[-_]?and[-_]?)?action/.test(normalizedPath) || /target[-_]?data/.test(normalizedPath)) {
      console.log('[routeComponents] Fallback regex matched target/actions path:', pathFallback);
      return componentMap['target_and_action'] || componentMap['target-data'] || componentMap['targetdata'];
    }

    console.log('[routeComponents] No match found for:', pathFallback);
  }

  return undefined;
}

/**
 * Preload all components on idle (for faster navigation)
 */
export function preloadAllComponents() {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      // Preload the main MFE route bundles
      import('analytics_mfe/AnalyticsRoutes').catch(() => {});
      import('esg_data_mfe/EsgRoutes').catch(() => {});
      import('reporting_mfe/ReportRoutes').catch(() => {});
      import('compliance_mfe/ComplianceRoutes').catch(() => {});
      import('sustainability_mfe/SustainabilityRoutes').catch(() => {});
      import('chat_mfe/ChatRoutes').catch(() => {});
      import('settings_mfe/SettingsRoutes').catch(() => {});
      import('lca_mfe/LcaRoutes').catch(() => {});
      import('dmrv_mfe/DmrvRoutes').catch(() => {});
      import('green_finance_mfe/GreenFinanceRoutes').catch(() => {});
      import('connectors_mfe/ConnectorsRoutes').catch(() => {});
      import('pcf_mfe/PcfRoutes').catch(() => {});
    });
  }
}
